import React, { useEffect, useMemo, useState } from "react";
import { useMediaQuery } from "react-responsive";
import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import Loader from "@material-ui/core/CircularProgress";
import Dialog from "@material-ui/core/Dialog";

import SideBar from "../../components/SideBar";
import { Components } from "../../assets/Dimensions";

import CMSReportEditDialog from "../../components/Base/CMSReportEditDialog";
import {
  getCMSReports,
  getDeleteInfo,
  markReportAsDone,
  deleteQuestionCMS,
} from "../../api/CMSReports";

// Error-reported questions triage queue. Custom table (full row styling + sort),
// KPI cards, non-destructive "done" state, and report-count urgency highlighting.

const initialDeleteFlow = {
  open: false,
  phase: null,
  cropId: null,
  reportId: null,
  data: null,
};

// deleteinfo response `type` → delete-flow modal phase.
const TYPE_TO_PHASE = {
  Error: "error",
  Warning: "warning",
  Info: "info",
  Success: "confirm",
};

// "2026-06-18T16:14:00.362377" → "18.06.2026 16:14"
const formatDate = (v) => {
  if (!v) return "—";
  const d = new Date(v);
  if (isNaN(d.getTime())) return v;
  const pad = (n) => String(n).padStart(2, "0");
  return `${pad(d.getDate())}.${pad(d.getMonth() + 1)}.${d.getFullYear()} ${pad(
    d.getHours()
  )}:${pad(d.getMinutes())}`;
};

// "3 gün önce" / "dün" / "bugün"
const relativeDate = (v) => {
  if (!v) return "";
  const d = new Date(v);
  if (isNaN(d.getTime())) return "";
  const days = Math.floor((Date.now() - d.getTime()) / 86400000);
  if (days <= 0) return "bugün";
  if (days === 1) return "dün";
  if (days < 7) return `${days} gün önce`;
  if (days < 30) return `${Math.floor(days / 7)} hafta önce`;
  if (days < 365) return `${Math.floor(days / 30)} ay önce`;
  return `${Math.floor(days / 365)} yıl önce`;
};

// Headers kept identical to the API field names (no translation) so they map
// 1:1 to the response and stay unambiguous.
const COLUMN_DEFS = [
  { key: "ReportId", label: "ReportId", numeric: true, sortable: true },
  { key: "BookSectionCropId", label: "BookSectionCropId", numeric: true, sortable: true },
  { key: "BookSectionId", label: "BookSectionId", numeric: true, sortable: true },
  { key: "QuestionNumber", label: "QuestionNumber", numeric: true, sortable: true },
  { key: "TotalReportCount", label: "TotalReportCount", numeric: true, sortable: true },
  { key: "MaxDate", label: "MaxDate", type: "date", sortable: true },
  { key: "Status", label: "Status", sortable: false },
];

const applySort = (data, sort) => {
  if (!sort.key || !sort.dir) return data;
  const isDate = COLUMN_DEFS.find((c) => c.key === sort.key)?.type === "date";
  return [...data].sort((a, b) => {
    let va = a[sort.key];
    let vb = b[sort.key];
    if (isDate) {
      va = new Date(va).getTime() || 0;
      vb = new Date(vb).getTime() || 0;
    }
    if (typeof va === "number" && typeof vb === "number") {
      return sort.dir === "asc" ? va - vb : vb - va;
    }
    const cmp = String(va ?? "").localeCompare(String(vb ?? ""), "tr", {
      sensitivity: "base",
    });
    return sort.dir === "asc" ? cmp : -cmp;
  });
};

// Higher report count = more users complained = more urgent.
const urgencyBg = (count) => {
  if (count >= 5) return "#fef2f2"; // red-50
  if (count >= 3) return "#fffbeb"; // amber-50
  return null;
};

const CMSReportsPage = () => {
  const isTabletOrMobile = useMediaQuery({ query: "(max-width: 700px)" });
  const user = JSON.parse(localStorage.getItem("user"));

  const [reports, setReports] = useState([]);
  const [loader, setLoader] = useState(false);

  // Detail modal
  const [detailOpen, setDetailOpen] = useState(false);
  const [selectedCropId, setSelectedCropId] = useState(null);

  // Delete-question flow
  const [deleteFlow, setDeleteFlow] = useState(initialDeleteFlow);

  // UI state
  const [doneIds, setDoneIds] = useState(() => new Set()); // ReportIds resolved this session
  const [deletedCount, setDeletedCount] = useState(0);
  const [hideDone, setHideDone] = useState(false);
  const [sort, setSort] = useState({ key: null, dir: null });
  const [activeRowId, setActiveRowId] = useState(null); // last row a button was clicked on
  const [copied, setCopied] = useState(false);

  const containerStyle = {
    paddingLeft: isTabletOrMobile ? 15 : Components.SideBarWidth + 15,
    paddingTop: 20,
    paddingRight: 15,
    marginBottom: 40,
  };

  // ─── Load ─────────────────────────────────────────────────────────────────

  const loadReports = async () => {
    try {
      setLoader(true);
      const res = await getCMSReports({
        StatusType: 0,
        MonthCount: 0,
        OrderBy: 1
    }, user.Token);
      if (res.status === 200) {
        const data = Array.isArray(res.data?.ResponseData)
          ? res.data.ResponseData
          : [];
        setReports(data);
      } else {
        toast.error("Raporlar getirilirken bir sorun oluştu.");
        setReports([]);
      }
    } catch (error) {
      console.error("CMSReports loadReports error:", error);
      toast.error("Raporlar getirilirken bir sorun oluştu.");
    } finally {
      setLoader(false);
    }
  };

  useEffect(() => {
    loadReports();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // ─── Derived ──────────────────────────────────────────────────────────────

  const pendingReports = useMemo(
    () => reports.filter((r) => !doneIds.has(r.ReportId)),
    [reports, doneIds]
  );

  const kpis = useMemo(
    () => [
      {
        label: "Bekleyen Hatalı Soru",
        value: pendingReports.length,
        color: "#dc2626",
      },
      {
        label: "Toplam Rapor",
        value: pendingReports.reduce((s, r) => s + (r.TotalReportCount || 0), 0),
        color: "#2563eb",
      },
      {
        label: "Etkilenen BookSectionId",
        value: new Set(pendingReports.map((r) => r.BookSectionId)).size,
        color: "#7c3aed",
      },
      {
        label: "Bu Oturumda Çözülen",
        value: doneIds.size + deletedCount,
        color: "#16a34a",
      },
    ],
    [pendingReports, doneIds, deletedCount]
  );

  const sortedReports = useMemo(() => {
    const base = hideDone
      ? reports.filter((r) => !doneIds.has(r.ReportId))
      : reports;
    return applySort(base, sort);
  }, [reports, doneIds, hideDone, sort]);

  // ─── Detail modal ───────────────────────────────────────────────────────────

  const openDetail = (row) => {
    setActiveRowId(row.ReportId);
    setSelectedCropId(row.BookSectionCropId);
    setDetailOpen(true);
  };

  // ─── Mark as done (non-destructive: row stays, flips to a resolved state) ──────

  const handleMarkDone = async (row) => {
    setActiveRowId(row.ReportId);
    try {
      setLoader(true);
      const res = await markReportAsDone(row.BookSectionCropId, user.Token);
      if (res.status === 200) {
        toast.success("Soru tamamlandı olarak işaretlendi.");
        setDoneIds((prev) => new Set(prev).add(row.ReportId));
      } else {
        toast.error("İşlem başarısız oldu.");
      }
    } catch (error) {
      console.error("CMSReports handleMarkDone error:", error);
      toast.error("İşlem başarısız oldu.");
    } finally {
      setLoader(false);
    }
  };

  // ─── Delete-question flow ─────────────────────────────────────────────────────

  const closeDeleteFlow = () => {
    setDeleteFlow(initialDeleteFlow);
    setCopied(false);
  };

  // Step 1 — precheck (booksectioncrop/deleteinfo) and pick the modal to show.
  const startDelete = async (row) => {
    setActiveRowId(row.ReportId);
    setDeleteFlow({
      open: true,
      phase: "checking",
      cropId: row.BookSectionCropId,
      reportId: row.ReportId,
      data: null,
    });
    try {
      const res = await getDeleteInfo(row.BookSectionCropId, user.Token);
      const rd = res?.data?.ResponseData;
      if (!rd) {
        setDeleteFlow({
          open: true,
          phase: "error",
          cropId: row.BookSectionCropId,
          reportId: row.ReportId,
          data: {
            Message:
              res?.data?.ResponseMessage || "Soru silme kontrolü yapılamadı.",
          },
        });
        return;
      }
      const phase = TYPE_TO_PHASE[rd.Type] || "confirm";
      setDeleteFlow({
        open: true,
        phase,
        cropId: row.BookSectionCropId,
        reportId: row.ReportId,
        data: rd,
      });
    } catch (error) {
      console.error("CMSReports startDelete error:", error);
      toast.error("Kontrol sırasında bir hata oluştu.");
      closeDeleteFlow();
    }
  };

  // Step 2 — actually delete (only reachable from "info" / "confirm" phases).
  const confirmDelete = async () => {
    const { cropId, reportId } = deleteFlow;
    try {
      setLoader(true);
      const res = await deleteQuestionCMS(cropId, user.Token);
      if (res.status === 200) {
        toast.success("Soru silindi.");
        setReports((prev) => prev.filter((r) => r.ReportId !== reportId));
        setDeletedCount((c) => c + 1);
        closeDeleteFlow();
      } else {
        toast.error("Soru silinemedi.");
      }
    } catch (error) {
      console.error("CMSReports confirmDelete error:", error);
      toast.error("Soru silinemedi.");
    } finally {
      setLoader(false);
    }
  };

  // ─── Copy (warning modal) ─────────────────────────────────────────────────────

  const buildCopyText = () => {
    const row = reports.find((r) => r.ReportId === deleteFlow.reportId) || {};
    const d = deleteFlow.data || {};
    return [
      `ReportId: ${row.ReportId ?? deleteFlow.reportId ?? "-"}`,
      `BookSectionCropId: ${row.BookSectionCropId ?? deleteFlow.cropId ?? "-"}`,
      row.BookSectionId != null ? `BookSectionId: ${row.BookSectionId}` : null,
      row.QuestionNumber != null ? `QuestionNumber: ${row.QuestionNumber}` : null,
      "",
      d.Message || "Bu soru bazı alanlarda kullanılmaktadır.",
      ...(d.UsedAreas || []).map((u) => `- ${u.Message}`),
    ]
      .filter((l) => l !== null)
      .join("\n");
  };

  const copyToClipboard = async (text) => {
    try {
      if (navigator.clipboard && window.isSecureContext) {
        await navigator.clipboard.writeText(text);
      } else {
        // Fallback for non-secure contexts (clipboard API needs https/localhost)
        const ta = document.createElement("textarea");
        ta.value = text;
        ta.style.position = "fixed";
        ta.style.opacity = "0";
        document.body.appendChild(ta);
        ta.focus();
        ta.select();
        document.execCommand("copy");
        document.body.removeChild(ta);
      }
      setCopied(true);
      setTimeout(() => setCopied(false), 1800);
      toast.success("Bilgiler kopyalandı.");
    } catch (error) {
      console.error("CMSReports copy error:", error);
      toast.error("Kopyalanamadı.");
    }
  };

  // ─── Sorting ──────────────────────────────────────────────────────────────────

  const handleSort = (col) => {
    if (!col.sortable) return;
    setSort((prev) => {
      if (prev.key !== col.key) return { key: col.key, dir: "asc" };
      if (prev.dir === "asc") return { key: col.key, dir: "desc" };
      return { key: null, dir: null };
    });
  };

  const sortIcon = (key) =>
    sort.key !== key ? "↕" : sort.dir === "asc" ? "↑" : "↓";

  // ─── Cell renderer ────────────────────────────────────────────────────────────

  const renderCell = (col, row, isDone) => {
    const v = row[col.key];
    if (col.key === "MaxDate") {
      return (
        <div>
          <div>{formatDate(v)}</div>
          <div style={{ fontSize: 11, color: "#9ca3af" }}>{relativeDate(v)}</div>
        </div>
      );
    }
    if (col.key === "Status") {
      return isDone ? (
        <span style={styles.doneBadge}>Tamamlandı</span>
      ) : (
        <span style={styles.pendingBadge}>Bekliyor</span>
      );
    }
    if (col.key === "TotalReportCount") {
      const c = v >= 5 ? "#dc2626" : v >= 3 ? "#b45309" : "#111827";
      return <span style={{ fontWeight: 700, color: c }}>{v}</span>;
    }
    return v;
  };

  // ─── Render ─────────────────────────────────────────────────────────────────

  return (
    <div>
      {!isTabletOrMobile && <SideBar />}

      {loader && (
        <div style={styles.loaderOverlay}>
          <Loader size={100} />
        </div>
      )}

      <div style={containerStyle}>
        {/* Header */}
        <div style={styles.header}>
          <div style={{ fontWeight: "bold", fontSize: 22, color: "#111827" }}>
            CMS Reports
          </div>
          <div style={{ display: "flex", alignItems: "center", gap: 16 }}>
            <label style={styles.toggle}>
              <input
                type="checkbox"
                checked={hideDone}
                onChange={(e) => setHideDone(e.target.checked)}
                style={{ width: 15, height: 15, cursor: "pointer" }}
              />
              Tamamlananları gizle
            </label>
            <button onClick={loadReports} disabled={loader} style={styles.refreshBtn}>
              ↺ Yenile
            </button>
          </div>
        </div>

        {/* KPI cards */}
        <div style={styles.kpiRow}>
          {kpis.map((c) => (
            <div key={c.label} style={{ ...styles.kpiCard, borderTopColor: c.color }}>
              <div style={{ ...styles.kpiValue, color: c.color }}>{c.value}</div>
              <div style={styles.kpiLabel}>{c.label}</div>
            </div>
          ))}
        </div>

        {/* Table */}
        {reports.length === 0 && !loader ? (
          <div style={styles.empty}>
            Düzeltilmeyi bekleyen hatalı soru bulunmamaktadır.
          </div>
        ) : (
          <div style={styles.tableWrap}>
            <table style={styles.table}>
              <thead>
                <tr>
                  {COLUMN_DEFS.map((col) => (
                    <th
                      key={col.key}
                      onClick={() => handleSort(col)}
                      style={{
                        ...styles.th,
                        textAlign: col.numeric ? "right" : "left",
                        cursor: col.sortable ? "pointer" : "default",
                      }}
                    >
                      {col.label}
                      {col.sortable && (
                        <span
                          style={{
                            marginLeft: 6,
                            opacity: sort.key === col.key ? 1 : 0.35,
                            fontSize: 11,
                          }}
                        >
                          {sortIcon(col.key)}
                        </span>
                      )}
                    </th>
                  ))}
                  <th style={{ ...styles.th, textAlign: "right" }}>İşlemler</th>
                </tr>
              </thead>
              <tbody>
                {sortedReports.length === 0 ? (
                  <tr>
                    <td
                      colSpan={COLUMN_DEFS.length + 1}
                      style={{ textAlign: "center", padding: 30, color: "#6b7280" }}
                    >
                      Gösterilecek kayıt yok.
                    </td>
                  </tr>
                ) : (
                  sortedReports.map((row, idx) => {
                    const isDone = doneIds.has(row.ReportId);
                    const isActive = activeRowId === row.ReportId;
                    const stripe = idx % 2 === 0 ? "#ffffff" : "#f8fafc";
                    const bg = isDone
                      ? "#ecfdf5"
                      : isActive
                      ? "#dbeafe"
                      : urgencyBg(row.TotalReportCount || 0) || stripe;
                    return (
                      <tr
                        key={row.ReportId}
                        style={{ backgroundColor: bg, borderBottom: "1px solid #e2e8f0" }}
                      >
                        {COLUMN_DEFS.map((col, ci) => (
                          <td
                            key={col.key}
                            style={{
                              ...styles.td,
                              textAlign: col.numeric ? "right" : "left",
                              opacity: isDone && col.key !== "Status" ? 0.6 : 1,
                              ...(ci === 0 && isActive
                                ? { boxShadow: "inset 4px 0 0 0 #2563eb" }
                                : {}),
                            }}
                          >
                            {renderCell(col, row, isDone)}
                          </td>
                        ))}
                        <td style={{ ...styles.td, textAlign: "right" }}>
                          {isDone ? (
                            <div style={styles.rowActions}>
                              <button
                                style={{ ...styles.actionBtn, ...styles.viewBtn }}
                                onClick={() => openDetail(row)}
                              >
                                İncele
                              </button>
                              <span style={{ ...styles.actionBtn, ...styles.doneTag }}>
                                ✓ Tamamlandı
                              </span>
                            </div>
                          ) : (
                            <div style={styles.rowActions}>
                              <button
                                style={{ ...styles.actionBtn, ...styles.viewBtn }}
                                onClick={() => openDetail(row)}
                              >
                                İncele
                              </button>
                              <button
                                style={{ ...styles.actionBtn, ...styles.doneBtn }}
                                onClick={() => handleMarkDone(row)}
                              >
                                Tamamla
                              </button>
                              <button
                                style={{ ...styles.actionBtn, ...styles.deleteBtn }}
                                onClick={() => startDelete(row)}
                              >
                                Soruyu Sil
                              </button>
                            </div>
                          )}
                        </td>
                      </tr>
                    );
                  })
                )}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Detail modal (no Mark-as-Done / Delete buttons inside) */}
      <CMSReportEditDialog
        id="cmsReportDetail"
        user={user}
        isOpen={detailOpen}
        onClose={() => setDetailOpen(false)}
        selectedCropId={selectedCropId}
      />

      {/* Delete-question flow modal */}
      <Dialog open={deleteFlow.open} onClose={closeDeleteFlow}>
        <div style={styles.deleteModal}>
          <button
            style={styles.closeX}
            aria-label="Kapat"
            onClick={closeDeleteFlow}
            onMouseEnter={(e) => {
              e.currentTarget.style.color = "#111827";
              e.currentTarget.style.backgroundColor = "#f3f4f6";
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.color = "#9ca3af";
              e.currentTarget.style.backgroundColor = "transparent";
            }}
          >
            ✕
          </button>
          {deleteFlow.phase === "checking" && (
            <div style={{ display: "flex", flexDirection: "column", alignItems: "center", gap: 12 }}>
              <Loader size={36} />
              <span>Kontrol ediliyor...</span>
            </div>
          )}

          {deleteFlow.phase === "error" && (
            <>
              <div style={styles.titleError}>Soru Silinemez</div>
              <p style={{ color: "#b91c1c" }}>
                {deleteFlow.data?.Message || "Bu soru silinemez."}
              </p>
              <div style={styles.modalButtons}>
                <button style={styles.closeBtn} onClick={closeDeleteFlow}>Kapat</button>
              </div>
            </>
          )}

          {deleteFlow.phase === "warning" && (
            <>
              <div style={styles.titleWarning}>Soru Başka Alanlarda Kullanılıyor</div>
              <p>
                {deleteFlow.data?.Message ||
                  "Bu soru bazı alanlarda kullanılmaktadır."}
              </p>
              <ul style={{ margin: "8px 0", paddingLeft: 20, textAlign: "left" }}>
                {(deleteFlow.data?.UsedAreas || []).map((u, idx) => (
                  <li key={idx} style={{ marginBottom: 4 }}>
                    {u.Message}
                  </li>
                ))}
              </ul>
              <div style={styles.modalButtons}>
                <button
                  style={{ ...styles.copyBtn, ...(copied ? styles.copyBtnDone : {}) }}
                  onClick={() => copyToClipboard(buildCopyText())}
                >
                  {copied ? "✓ Kopyalandı" : "📋 Kopyala"}
                </button>
                <button style={styles.closeBtn} onClick={closeDeleteFlow}>Kapat</button>
              </div>
            </>
          )}

          {deleteFlow.phase === "info" && (
            <>
              <div style={styles.titleInfo}>Kazanım Sorusu</div>
              <p>
                {deleteFlow.data?.Message ||
                  "Silinmek istenilen soru achievement sorusudur."}
              </p>
              <ul style={{ margin: "8px 0", paddingLeft: 0, listStyleType: "none" }}>
                {(deleteFlow.data?.AchievementInfos || []).map((a, idx) => (
                  <li key={idx} style={{ fontSize: 16, fontWeight: 700, marginBottom: 6 }}>
                    Kazanım {a.AchievementId}: {a.DisplayText}
                  </li>
                ))}
              </ul>
              <p style={{ fontSize: 13, color: "#6b7280" }}>
                Silme sonrası kazanımdaki soru sayısı yukarıdaki gibi olacaktır.
                Devam etmek istiyor musunuz?
              </p>
              <div style={styles.modalButtons}>
                <button style={styles.cancelBtn} onClick={closeDeleteFlow}>Vazgeç</button>
                <button style={styles.confirmBtn} onClick={confirmDelete}>Sil</button>
              </div>
            </>
          )}

          {deleteFlow.phase === "confirm" && (
            <>
              <div style={styles.titleConfirm}>Soruyu Sil</div>
              <p>Artık bu soruyu kullanıcılar göremeyecek.</p>
              <p>Silmek istediğinize emin misiniz?</p>
              <div style={styles.modalButtons}>
                <button style={styles.cancelBtn} onClick={closeDeleteFlow}>Vazgeç</button>
                <button style={styles.confirmBtn} onClick={confirmDelete}>Sil</button>
              </div>
            </>
          )}
        </div>
      </Dialog>

      <ToastContainer />
    </div>
  );
};

const styles = {
  header: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    marginBottom: 20,
  },
  toggle: {
    display: "flex",
    alignItems: "center",
    gap: 6,
    fontSize: 13,
    color: "#374151",
    cursor: "pointer",
    userSelect: "none",
  },
  refreshBtn: {
    padding: "8px 16px",
    backgroundColor: "#2563eb",
    color: "#fff",
    border: "none",
    borderRadius: 6,
    cursor: "pointer",
    fontWeight: 600,
    fontSize: 13,
  },
  kpiRow: {
    display: "flex",
    gap: 14,
    flexWrap: "wrap",
    marginBottom: 22,
  },
  kpiCard: {
    flex: "1 1 160px",
    minWidth: 140,
    backgroundColor: "#fff",
    borderRadius: 10,
    borderTop: "3px solid #2563eb",
    boxShadow: "0 1px 4px rgba(0,0,0,0.08)",
    padding: "14px 18px",
  },
  kpiValue: { fontSize: 28, fontWeight: 800, lineHeight: 1.1 },
  kpiLabel: { fontSize: 13, color: "#6b7280", marginTop: 4 },
  empty: {
    padding: 40,
    textAlign: "center",
    color: "#6b7280",
    fontSize: 15,
    backgroundColor: "#f9fafb",
    borderRadius: 8,
    border: "1px dashed #d1d5db",
  },
  tableWrap: {
    overflowX: "auto",
    borderRadius: 8,
    boxShadow: "0 1px 4px rgba(0,0,0,0.1)",
  },
  table: {
    width: "100%",
    borderCollapse: "collapse",
    fontSize: 13,
    backgroundColor: "#fff",
  },
  th: {
    backgroundColor: "#1e3a5f",
    color: "#fff",
    fontWeight: 600,
    padding: "11px 14px",
    whiteSpace: "nowrap",
    borderRight: "1px solid #2d5282",
    userSelect: "none",
  },
  td: {
    padding: "8px 14px",
    borderRight: "1px solid #e2e8f0",
    color: "#374151",
    whiteSpace: "nowrap",
  },
  doneBadge: {
    backgroundColor: "#16a34a",
    color: "#fff",
    borderRadius: 12,
    padding: "2px 10px",
    fontSize: 12,
    fontWeight: 700,
  },
  pendingBadge: {
    backgroundColor: "#f3f4f6",
    color: "#6b7280",
    borderRadius: 12,
    padding: "2px 10px",
    fontSize: 12,
    fontWeight: 600,
  },
  loaderOverlay: {
    position: "fixed",
    top: "50%",
    left: "50%",
    transform: "translate(-50%, -50%)",
    zIndex: 9999,
  },
  rowActions: {
    display: "flex",
    gap: 6,
    justifyContent: "flex-end",
    whiteSpace: "nowrap",
  },
  actionBtn: {
    padding: "5px 10px",
    borderRadius: 6,
    fontSize: 12,
    fontWeight: 600,
    cursor: "pointer",
    border: "1px solid transparent",
    whiteSpace: "nowrap",
  },
  viewBtn: {
    backgroundColor: "#eff6ff",
    color: "#1d4ed8",
    borderColor: "#bfdbfe",
  },
  doneBtn: {
    backgroundColor: "#f0fdf4",
    color: "#15803d",
    borderColor: "#bbf7d0",
  },
  deleteBtn: {
    backgroundColor: "#fef2f2",
    color: "#dc2626",
    borderColor: "#fecaca",
  },
  doneTag: {
    backgroundColor: "#16a34a",
    color: "#fff",
    cursor: "default",
    borderColor: "#16a34a",
  },
  deleteModal: {
    position: "relative",
    padding: 24,
    paddingTop: 38,
    minWidth: 320,
    maxWidth: 440,
    textAlign: "center",
  },
  closeX: {
    position: "absolute",
    top: 8,
    right: 8,
    width: 30,
    height: 30,
    border: "none",
    background: "none",
    cursor: "pointer",
    fontSize: 18,
    lineHeight: 1,
    color: "#9ca3af",
    borderRadius: 6,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    zIndex: 10,
  },
  copyBtn: {
    padding: "8px 20px",
    borderRadius: 6,
    border: "1px solid #bfdbfe",
    backgroundColor: "#eff6ff",
    color: "#1d4ed8",
    cursor: "pointer",
    fontWeight: 600,
  },
  copyBtnDone: {
    border: "1px solid #16a34a",
    backgroundColor: "#f0fdf4",
    color: "#15803d",
  },
  titleError: { fontSize: 18, fontWeight: 700, color: "#b91c1c", marginBottom: 8 },
  titleWarning: { fontSize: 18, fontWeight: 700, color: "#b45309", marginBottom: 8 },
  titleInfo: { fontSize: 18, fontWeight: 700, color: "#1d4ed8", marginBottom: 8 },
  titleConfirm: { fontSize: 18, fontWeight: 700, color: "#111827", marginBottom: 8 },
  modalButtons: {
    display: "flex",
    justifyContent: "center",
    gap: 12,
    marginTop: 16,
  },
  closeBtn: {
    padding: "8px 20px",
    borderRadius: 6,
    border: "1px solid #d1d5db",
    backgroundColor: "#f9fafb",
    color: "#374151",
    cursor: "pointer",
    fontWeight: 600,
  },
  cancelBtn: {
    padding: "8px 20px",
    borderRadius: 6,
    border: "2px solid #16a34a",
    backgroundColor: "#fff",
    color: "#16a34a",
    cursor: "pointer",
    fontWeight: 600,
  },
  confirmBtn: {
    padding: "8px 20px",
    borderRadius: 6,
    border: "2px solid #dc2626",
    backgroundColor: "#fff",
    color: "#dc2626",
    cursor: "pointer",
    fontWeight: 600,
  },
};

export default CMSReportsPage;
